import type { AssetManifest, BootConfig, MapDocument, MapSummary, RuntimeConfig } from './types';

export class ApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly data?: Record<string, unknown>) {
    super(message);
  }
}

export class RestClient {
  constructor(private readonly boot: BootConfig) {}

  config(): Promise<RuntimeConfig> {
    return this.request<RuntimeConfig>('config');
  }

  assets(version?: number): Promise<AssetManifest> {
    return this.request<AssetManifest>(`assets${version ? `?version=${version}` : ''}`);
  }

  maps(): Promise<MapSummary[]> {
    return this.request<MapSummary[]>('maps');
  }

  getMap(uuid: string): Promise<MapDocument> {
    return this.request<MapDocument>(`maps/${encodeURIComponent(uuid)}`);
  }

  createMap(document: MapDocument): Promise<MapDocument> {
    return this.request<MapDocument>('maps', { method: 'POST', body: JSON.stringify(document) });
  }

  updateMap(document: MapDocument): Promise<MapDocument> {
    return this.request<MapDocument>(`maps/${encodeURIComponent(document.id)}?expectedRevision=${document.revision}`, {
      method: 'PUT',
      body: JSON.stringify(document),
    });
  }

  publishMap(uuid: string, publish: boolean): Promise<MapDocument> {
    return this.request<MapDocument>(`maps/${encodeURIComponent(uuid)}/publish`, {
      method: 'POST',
      body: JSON.stringify({ publish }),
    });
  }

  duplicateMap(uuid: string): Promise<MapDocument> {
    return this.request<MapDocument>(`maps/${encodeURIComponent(uuid)}/duplicate`, { method: 'POST' });
  }

  async exportMap(uuid: string): Promise<void> {
    const response = await this.request<{ url: string }>(`maps/${encodeURIComponent(uuid)}/export`);
    window.location.assign(response.url);
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');
    if (init.body) headers.set('Content-Type', 'application/json');
    if (this.boot.nonce) headers.set('X-WP-Nonce', this.boot.nonce);
    const response = await fetch(`${this.boot.restRoot}${path}`, { ...init, headers, credentials: 'same-origin' });
    if (!response.ok) {
      let payload: Record<string, unknown> = {};
      try { payload = await response.json() as Record<string, unknown>; } catch { /* no-op */ }
      throw new ApiError(String(payload.message || response.statusText), response.status, payload.data as Record<string, unknown> | undefined);
    }
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  }
}
